import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"]):not([disabled])'
].join(',');

/**
 * Focus trap pour modaux/dialogs (WCAG 2.1.2 No Keyboard Trap + 2.4.3 Focus Order).
 *
 * Phase 27.47 — port du hook EGSF pour ExitIntentPopup. Réutilisable pour
 * tout futur modal Océanne (CalendlyModal pourrait l'adopter aussi).
 *
 * Usage :
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, isOpen);
 *   return <div ref={ref} role="dialog">...</div>;
 *
 * Comportement :
 *  - À l'ouverture : focus le premier élément focusable du conteneur
 *  - Cycle Tab/Shift+Tab : reste à l'intérieur du conteneur
 *  - À la fermeture : restaure le focus sur l'élément qui avait le focus avant
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusFirst = () => {
      // Si autoFocus a déjà placé le focus dans le container, on ne l'override pas.
      if (container.contains(document.activeElement)) return;
      const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        container.focus();
      }
    };

    // Délai 1 frame pour laisser le DOM stabiliser (modals avec animations)
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        // preventScroll évite un scroll surprise quand l'élément précédemment
        // focus est hors du viewport actuel.
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [containerRef, active]);
}
